// @ts-nocheck
import { getFocalPointStyles } from "@insite/client-framework/Common/BasicSelectors";
import parseImageUrl from "@insite/client-framework/Common/Utilities/parseImageUrl";
import Zone from "@insite/client-framework/Components/Zone";
import { ColumnAlignment } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import * as cssLinter from "css";
import * as React from "react";
// import { css } from "styled-components"; // Starter Custom Code

/* Starter Custom Code - Start */
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import getGapPresetField from "@store/Framework/Common/Fields/Toolkit/gapPreset";
import getVerticalGapField from "@store/Framework/Common/Fields/Toolkit/verticalGap";
// import Block from "@store/Library/Components/Toolkit/Block"; // Foundation
import blockFullWidthField from "@store/Library/Components/Toolkit/Block/fields/fullWidth";
import blockMarginField from "@store/Library/Components/Toolkit/Block/fields/margin";
import blockPreserveSpacingField from "@store/Library/Components/Toolkit/Block/fields/preserveSpacing";
import blockVariantField from "@store/Library/Components/Toolkit/Block/fields/variant";
import blockVariantSideField from "@store/Library/Components/Toolkit/Block/fields/variantSide";
// import { getGridPreset } from "@toolkit/Framework/Common/Utilities/gridPreset";
// import { css } from "styled-components"; // Foundation
/* Starter Custom Code - End */

// Store --->
import useZoneIsActive from "@store/Framework/Common/Hooks/useZoneIsActive"; // Store Custom Code
import useBackgroundSkeleton from "@store/Framework/Common/Hooks/useBackgroundSkeleton"; // Store Custom Code

import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block/Block"; // Store Custom Code
import { css, useTheme } from "styled-components";
// <--- Store

// Store Custom Code
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getGridPreset } from "@store/Framework/Common/Utilities/gridPreset";

const enum fields {
    columns = "columns",
    gap = "gap",
    alignment = "alignment",
    reflow = "reflow",
    isFullWidth = "isFullWidth",
    fullWidthStylingType = "fullWidthStylingType",
    fullWidthColor = "fullWidthColor",
    fullWidthImage = "fullWidthImage",
    fullWidthImageFocalPoint = "fullWidthImageFocalPoint",
    customCSS = "customCSS",
    // Starter Custom Code
    gapPreset = "gapPreset",
    blockPreserveSpacing = "blockPreserveSpacing",
    blockFullWidth = "blockFullWidth",
    blockVariant = "blockVariant",
    blockVariantSide = "blockVariantSide",
    blockMargin = "blockMargin",
    gridVerticalGap = "gridVerticalGap",
    // Foundation
    removeVerticalSpacing = "removeVerticalSpacing",
    removeSpacing = "removeSpacing",
    // Store Custom Code
    enableSkeleton = "enableSkeleton",
}

/* Starter Custom Code - Start */
const gapPresetField = getGapPresetField(fields.gapPreset);
const gapField = getCssUnitField(fields.gap, { shorthands: ["row", "column"] });
const verticalGapField = getVerticalGapField(fields.gridVerticalGap);
/* Starter Custom Code - End */

interface Props extends WidgetProps {
    fields: {
        [fields.columns]: GridWidths[];
        [fields.gap]: number;
        [fields.alignment]: ColumnAlignment[];
        [fields.reflow]: boolean;
        [fields.isFullWidth]: boolean;
        [fields.fullWidthStylingType]: string;
        [fields.fullWidthColor]: string;
        [fields.fullWidthImage]: string;
        [fields.fullWidthImageFocalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.customCSS]: string;
        // Starter Custom Code
        [fields.gapPreset]: string;
        [fields.blockPreserveSpacing]: boolean;
        [fields.blockFullWidth]: boolean;
        [fields.blockVariant]: string;
        [fields.blockVariantSide]: string;
        [fields.blockMargin]: string;
        [fields.gridVerticalGap]: string;
        // Foundation
        [fields.removeVerticalSpacing]: boolean;
        [fields.removeSpacing]: boolean;
        // Store Custom Code
        [fields.enableSkeleton]: boolean;
    };
}

/* Starter Custom Code - Start */
const mobileColumnsRef: { [key: number]: GridWidths | GridWidths[] } = {
    1: [3, 2, 1, 1, 1],
    2: [4, 3, 2, 2, 2],
    3: [6, 3, 3, 3, 3],
    4: [6, 4, 4, 4, 4],
    5: [12, 5, 5, 5, 5],
    6: [12, 6, 6, 6, 6],
    7: [12, 7, 7, 7, 7],
    8: [12, 8, 8, 8, 8],
    9: [12, 9, 9, 9, 9],
    10: [12, 9, 10, 10, 10],
    11: [12, 10, 11, 11, 11],
    12: 12,
};
/* Starter Custom Code - End */

const convertToResponsive = (column: GridWidths): GridWidths | GridWidths[] => {
    return mobileColumnsRef[column] || column;
};

const Row = (props: Props) => {
    const {
        fields: {
            columns,
            gap,
            alignment,
            reflow,
            isFullWidth,
            fullWidthStylingType,
            fullWidthColor,
            fullWidthImage,
            fullWidthImageFocalPoint,
            customCSS,
            // Starter Custom Code
            gapPreset,
            blockPreserveSpacing,
            blockFullWidth,
            blockVariant,
            blockVariantSide,
            blockMargin,
            gridVerticalGap,
            // Foundation
            removeSpacing,
            removeVerticalSpacing,
            // Store Custom Code
            enableSkeleton,
        },
        id,
    } = props;

    const theme = useTheme(); // Store Custom Code

    // Store Custom Code - preload background image with shimmer skeleton
    const parsedFullWidthImage =
        enableSkeleton && isFullWidth && fullWidthStylingType === "image" && fullWidthImage
            ? parseImageUrl(fullWidthImage).url
            : undefined;
    const { css: bgSkeletonCss } = useBackgroundSkeleton(parsedFullWidthImage);

    const columnElements: JSX.Element[] = [];

    /* Starter Custom Code - Start */
    const gapPresetData = gapPresetField.useParse(gapPreset);
    const gapFieldData = gapField.useParseToGridPreset(gap);
    const verticalGapFieldData = verticalGapField.useParse(gridVerticalGap);

    const zoneIsActive = [];
    for (let x = 0; x < 12; x++) {
        zoneIsActive.push(useZoneIsActive(id, `Content${x}`));
    }

    for (let x = 0; x < columns.length; x++) {
        if (zoneIsActive[x]) {
            const align = alignment[x] ? alignment[x] : "top";
            columnElements.push(
                <GridItem
                    key={x}
                    width={reflow ? convertToResponsive(columns[x]) : columns[x]}
                    align={align}
                    css={css`
                        > div /* Zone */ {
                            height: auto;
                        }
                    `}
                >
                    <Zone contentId={id} zoneName={`Content${x}`} />
                </GridItem>,
            );
        }
    }

    if (!columnElements.length) {
        return null;
    }
    /* Starter Custom Code - End */

    const fullWidthBackgroundCss = !isFullWidth
        ? undefined
        : fullWidthStylingType === "solidColor"
          ? css`
                background-color: ${fullWidthColor};
            `
          : fullWidthStylingType === "image"
            ? enableSkeleton
                ? css`
                      ${bgSkeletonCss}
                      background-size: cover;
                      ${getFocalPointStyles(fullWidthImageFocalPoint)}
                  `
                : css`
                      background-image: url("${parseImageUrl(fullWidthImage).url}");
                      background-size: cover;
                      ${getFocalPointStyles(fullWidthImageFocalPoint)}
                  `
            : undefined;

    const finalCss = css`
        ${fullWidthBackgroundCss}
        ${customCSS}
    `;
    // Foundation --->
    const blockProps: BlockProps = {
        preserveSpacing: blockPreserveSpacing,
        fullWidth: blockFullWidth,
        variant: blockVariant,
        variantSide: blockVariantSide,
        margin: removeVerticalSpacing ? ["0"] : theme.design.layout.spacing.content,
    };
    // <--- Foundation

    /* Starter Custom Code - Start */
    const gridContainerProps = getGridPreset({
        vertical: gapPresetData?.vertical || verticalGapFieldData || gapFieldData?.vertical || [],
        horizontal: gapPresetData?.horizontal || gapFieldData?.horizontal || [],
    });
    /* Starter Custom Code - End */

    /* Store Custom Code - Start */
    return (
        <StyledWrapper css={finalCss}>
            <Block {...blockProps /* Foundation */}>
                <GridContainer {...gridContainerProps}>{columnElements}</GridContainer>
            </Block>
        </StyledWrapper>
    );
    /* Store Custom Code - End */
};

const defaultCustomCss = `& {

}`;

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> /* Starter Custom Code */ = {
    component: Row,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.columns,
                displayName: "Layout",
                editorTemplate: "CustomColumnsField", // Starter Custom Code
                defaultValue: [12], // Starter Custom Code
                fieldType: "General",
                sortOrder: 1,
                validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            },
            {
                name: fields.reflow,
                displayName: "Use default reflow for responsive screens sizes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                ...gapField.definition, // Starter Custom Code
                // Starter Removed - name
                displayName: "Gap Between Items",
                // Starter Removed - editorTemplate
                // Starter Removed - defaultValue
                // Starter Removed - fieldType
                // Starter Removed - max
                // Starter Removed - min
                sortOrder: 2,
                isVisible: widget => !widget.fields.gapPreset, // Starter Custom Code
            },
            // Foundation  --->
            {
                name: fields.removeVerticalSpacing,
                displayName: "Remove vertical spacing",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 104,
                tab: advancedTab,
            },
            {
                name: fields.removeSpacing,
                displayName: "Remove spacing on full screen width",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 104,
                tab: advancedTab,
                isVisible: (item: HasFields) => item.fields.blockFullWidth,
            },
            // <--- Foundation
            {
                name: fields.isFullWidth,
                displayName: "Show Background", // Starter Custom Code
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                // Starter Removed - isVisible
                sortOrder: 3,
            },
            {
                name: fields.fullWidthStylingType,
                displayName: "Styling",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Solid Color",
                        value: "solidColor",
                    },
                    {
                        displayName: "Image",
                        value: "image",
                    },
                ],
                defaultValue: "solidColor",
                horizontal: true,
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth } }, _advancedFeaturesEnabled, theme) => !!isFullWidth, // Starter Custom Code
                sortOrder: 4,
            },
            {
                name: fields.fullWidthColor,
                displayName: "Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "transparent",
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!isFullWidth && fullWidthStylingType === "solidColor", // Starter Custom Code
                sortOrder: 5,
            },
            {
                name: fields.fullWidthImage,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!isFullWidth && fullWidthStylingType === "image", // Starter Custom Code
                sortOrder: 6,
            },
            {
                name: fields.fullWidthImageFocalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                fieldType: "General",
                hideEmptyOption: true,
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }, _advancedFeaturesEnabled, theme) =>
                    !!isFullWidth && fullWidthStylingType === "image", // Starter Custom Code
                sortOrder: 7,
            },
            /* Store Custom Code - Start */
            {
                name: fields.enableSkeleton,
                displayName: "Enable Loading Skeleton",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isVisible: ({ fields: { isFullWidth, fullWidthStylingType } }) =>
                    !!isFullWidth && fullWidthStylingType === "image",
                sortOrder: 8,
            },
            /* Store Custom Code - End */
            {
                name: fields.alignment,
                displayName: "Column Alignment",
                editorTemplate: "CustomColumnAlignmentField", // Starter Custom Code
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                tab: advancedTab,
            },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            /* Starter Custom Code - Start */
            {
                ...gapPresetField.definition,
                sortOrder: 1,
            },
            {
                ...blockVariantField,
                name: fields.blockVariant,
                sortOrder: 100,
                tab: advancedTab,
                isVisible: widget => !widget.fields.isFullWidth,
            },
            {
                ...blockVariantSideField,
                name: fields.blockVariantSide,
                sortOrder: 101,
                tab: advancedTab,
            },
            {
                ...verticalGapField.definition,
                sortOrder: 102,
                isVisible: widget => !widget.fields.gapPreset,
                tab: advancedTab,
            },
            {
                ...blockFullWidthField,
                name: fields.blockFullWidth,
                sortOrder: 2,
            },
            {
                ...blockMarginField,
                name: fields.blockMargin,
                sortOrder: 104,
                tab: advancedTab,
            },
            {
                ...blockPreserveSpacingField,
                name: fields.blockPreserveSpacing,
                sortOrder: 105,
                tab: advancedTab,
            },
            /* Starter Custom Code - End */
        ],
    },
};

export default widgetModule;
